/* ===================================================================
   useCustomerActivity(id) -- a single customer's activity timeline for
   the customer profile modal.

   Binds to GET /api/v1/customers/{id}/activity (engine/api/v1/app.py)
   through the typed api client (src/lib/api.ts). Disabled until a Bearer
   rr_ token exists AND a customer id is set, so it only fires when the
   modal opens for a specific customer (pass null/undefined to keep it
   idle). Empty history returns an empty events list -- never invented.
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type CustomerActivityResponse } from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export interface UseCustomerActivityParams {
  /** Force-disable even if a token + id exist. */
  enabled?: boolean;
}

export function useCustomerActivity(
  customerId: string | null | undefined,
  params: UseCustomerActivityParams = {},
): UseQueryResult<CustomerActivityResponse, Error> {
  const hasToken = Boolean(useApiToken());
  const id = customerId ?? '';

  return useQuery({
    queryKey: qk.customerActivity(id),
    // Only runs when enabled (id is non-empty); '' never reaches the network.
    queryFn: ({ signal }) => api.getCustomerActivity(id, signal),
    enabled: hasToken && Boolean(id) && params.enabled !== false,
    staleTime: 30_000,
  });
}
